import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Lock, ShieldCheck, CreditCard, Smartphone, Crown, Check } from 'lucide-react';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../lib/metaPixel';
import NucleusPayMobileMoney from './NucleusPayMobileMoney';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ActivationModal — Paywall 25 € / 16 000 FCFA "Mise en service".
 *
 * Forced French copy. Stripe Embedded (no redirect) + CinetPay redirect for
 * Cameroon Mobile Money. Meta CAPI "Purchase" event is fired server-side
 * after the webhook / confirm endpoint, deduped via event_id.
 */
export default function ActivationModal({ open, onClose, reason }) {
  const { user, getAuthHeaders } = useAuth();
  const [mode, setMode] = useState(null); // null | 'stripe' | 'cinetpay'
  const [busy, setBusy] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [txId, setTxId] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!open) {
      // Reset state when closing
      setMode(null); setClientSecret(null); setTxId(null);
      return;
    }
    axios.get(`${API}/activation/status`, { headers: getAuthHeaders() })
      .then(r => setInfo(r.data)).catch(() => {});
  }, [open, getAuthHeaders]);

  const country = ((user?.country || info?.country || '') + '').toLowerCase();
  void country;

  const startStripe = async () => {
    setBusy(true);
    trackEvent('InitiateCheckout', {
      value: 25, currency: 'EUR',
      content_name: 'Activation Nucleus 25€',
      email: user?.email, city: user?.city, country: user?.country,
    });
    try {
      const r = await axios.post(`${API}/activation/init-stripe`, {},
        { headers: getAuthHeaders() });
      if (r.data.publishable_key) setStripePromise(loadStripe(r.data.publishable_key));
      setClientSecret(r.data.client_secret);
      setTxId(r.data.transaction_id);
      setMode('stripe');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Stripe indisponible — utilisez Mobile Money');
    } finally {
      setBusy(false);
    }
  };

  const startCinetPay = null; // CinetPay removed — Mobile Money now via Nucleus Pay (pawaPay)
  void startCinetPay;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="activation-modal">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-800 text-[11px] font-bold uppercase tracking-[2px] w-fit">
            <Lock className="w-3.5 h-3.5" /> Activation Obligatoire
          </div>
          <DialogTitle className="font-display text-2xl mt-2">
            Activation Obligatoire de l'Établissement
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 leading-relaxed">
            Pour interagir avec la communauté, contacter les familles et certifier votre site internet,
            vous devez vous acquitter des <strong>frais de mise en service et de scoring ICF initial
            (25 € / 16 000 FCFA)</strong>. Cette étape est obligatoire pour être éligible au financement
            du mois prochain.
          </DialogDescription>
        </DialogHeader>

        {reason && mode === null && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            <strong>Action déclenchée :</strong> {reason}
          </div>
        )}

        {mode === null && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: ShieldCheck, t: 'Retrait du watermark', d: 'Votre site institutionnel devient public et certifié.' },
                { icon: Crown, t: 'Forum activé', d: 'Publiez et répondez sur le Conseil UB.' },
                { icon: Check, t: '1 message par famille', d: 'Initiez une conversation depuis le Bureau des Admissions.' },
                { icon: Check, t: 'Score ICF initialisé', d: '+150 points (Identité) calculés immédiatement.' },
              ].map((b, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-200 p-3">
                  <b.icon className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-oxford-800">{b.t}</p>
                    <p className="text-xs text-slate-600">{b.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                onClick={startStripe}
                disabled={busy}
                data-testid="activation-pay-stripe"
                className="bg-oxford-800 hover:bg-oxford-700 text-white h-14 text-base font-bold"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />}
                Payer 25 € · Carte
              </Button>
              <Button
                onClick={() => setMode('momo')}
                disabled={busy}
                data-testid="activation-pay-momo"
                className="bg-emerald-600 hover:bg-emerald-500 text-white h-14 text-base font-bold"
              >
                <Smartphone className="w-5 h-5 mr-2" />
                Payer · Mobile Money
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 text-center">
              Paiement sécurisé Nucleus Pay · Activation immédiate · Aucune redirection externe.
            </p>
          </div>
        )}

        {mode === 'momo' && (
          <div className="space-y-3">
            <Button
              variant="ghost" size="sm"
              onClick={() => setMode(null)}
              className="text-slate-500"
            >
              ← Retour
            </Button>
            <NucleusPayMobileMoney
              authHeader={getAuthHeaders}
              amountEur={25}
              kind="activation"
              entityId="activation"
              statementDescription="UBO Mise en service"
              onSuccess={async () => {
                toast.success('Activation confirmée ✓ Vous êtes désormais Observateur.');
                setTimeout(() => { onClose?.(); window.location.reload(); }, 1200);
              }}
            />
          </div>
        )}

        {mode === 'stripe' && clientSecret && stripePromise && (
          <div className="space-y-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setMode(null); setClientSecret(null); }}
              className="text-slate-500"
            >
              ← Retour
            </Button>
            <div className="rounded-xl border border-slate-200 p-4 bg-white">
              <Elements
                stripe={stripePromise}
                options={{ clientSecret, appearance: { theme: 'stripe' }, locale: 'fr' }}
              >
                <StripeForm txId={txId} onSuccess={onClose} />
              </Elements>
            </div>
            <p className="text-[11px] text-slate-500 text-center">
              Paiement carte intégré · Vos données ne quittent pas Nucleus.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StripeForm({ txId, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const { getAuthHeaders } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErrMsg(null);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });
    if (error) {
      setErrMsg(error.message || 'Paiement refusé. Vérifiez votre carte.');
      setSubmitting(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      try {
        await axios.post(`${API}/activation/confirm-stripe/${txId}`, {},
          { headers: getAuthHeaders() });
        toast.success('Activation confirmée ✓ Vous êtes désormais Observateur.');
        // Refresh app state — auth/me will return the new status.
        setTimeout(() => window.location.reload(), 1200);
      } catch {
        toast.error('Paiement reçu mais l\'activation n\'a pas été appliquée. Contactez le support.');
      }
      onSuccess?.();
    } else {
      setErrMsg('Paiement non confirmé. Réessayez.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="activation-stripe-form">
      <PaymentElement options={{ layout: 'tabs' }} />
      {errMsg && (
        <div className="rounded bg-rose-50 border border-rose-200 p-2 text-xs text-rose-700">{errMsg}</div>
      )}
      <Button
        type="submit"
        disabled={!stripe || submitting}
        data-testid="activation-stripe-pay-btn"
        className="w-full bg-oxford-800 hover:bg-oxford-700 text-white h-12 font-bold"
      >
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
        Confirmer le paiement · 25 €
      </Button>
    </form>
  );
}
